// You can write more code here

/* START OF COMPILED CODE */

import { sfx } from '@/common/utils/audio';
import Cursor from './Cursor';
/* START-USER-IMPORTS */
import { HorizontalLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/HorizontalLayoutGroup';
import {
  waitForMilliseconds,
  waitUntil,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { HOVER_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import SingleCharacterText from '@/game/prefabs/StudentGame/SingleCharacterText';
/* END-USER-IMPORTS */

export default class PlayerTypingInput extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);

    // typingBacking
    const typingBacking = scene.add.image(0, 0, 'Card-512');
    typingBacking.scaleX = 1.5;
    typingBacking.scaleY = 1.5;
    typingBacking.tintTopLeft = 16446431;
    typingBacking.tintTopRight = 16446431;
    typingBacking.tintBottomLeft = 16446431;
    typingBacking.tintBottomRight = 16446431;
    this.add(typingBacking);

    // textCharactersContainer
    const textCharactersContainer = scene.add.container(0, 0);
    this.add(textCharactersContainer);

    // cursor
    const cursor = new Cursor(scene, 0, -2);
    textCharactersContainer.add(cursor);

    this.typingBacking = typingBacking;
    this.cursor = cursor;
    this.textCharactersContainer = textCharactersContainer;

    /* START-USER-CTR-CODE */
    this.typedTextLayoutGroup = new HorizontalLayoutGroup({
      parent: this.textCharactersContainer,
      origin: new Phaser.Math.Vector2(0, 0),
      elementDist: 4,
      align: 'center',
    });
    this.initialize();
    /* END-USER-CTR-CODE */
  }

  public typingBacking: Phaser.GameObjects.Image;
  private cursor: Cursor;
  public textCharactersContainer: Phaser.GameObjects.Container;

  /* START-USER-CODE */

  private cursorIndex: number = 0;
  private typedTextLayoutGroup: HorizontalLayoutGroup;

  initialize() {
    // interactivity.
    this.typingBacking.setInteractive({ cursor: `${HOVER_CURSOR_PATH}` });
    this.typingBacking.on('pointerdown', (event: any) => {
      this.updateCursorPosFromUserClick(event);
    });
    this.typingBacking.setTexture('Card-512');
  }

  setCursorIndex(index: number) {
    this.cursorIndex = index;
    this.typedTextLayoutGroup.moveElement(this.cursor, index);
  }

  getCursorIndex() {
    return this.cursorIndex;
  }

  setIsInteractive(isInteractive: boolean) {
    this.cursor.setVisible(isInteractive);
    this.typedTextLayoutGroup.updateLayout();
  }

  getTypedTextLayoutGroup() {
    return this.typedTextLayoutGroup;
  }

  doSuccessFeedback() {
    this.typedTextLayoutGroup.getElementList().forEach((element) => {
      if (element instanceof SingleCharacterText) {
        element.doSuccessFeedback();
      }
    });
  }

  doIncorrectFeedback() {
    this.typedTextLayoutGroup.getElementList().forEach((element) => {
      if (element instanceof SingleCharacterText) {
        element.doIncorrectFeedback();
      }
    });
  }

  getCurrentPlayerInput(): string {
    return this.getCharacterArray()
      .map((element) => element.text)
      .join('');
  }

  async riffleLetters() {
    const letters = this.getCharacterArray();
    sfx.mastered();
    const totalTime = 400;
    for (var i = 0; i < letters.length; i++) {
      const letter = letters[i];
      if (!letter) continue;
      letter.doSuccessFeedback();
      await waitForMilliseconds(this.scene.time, totalTime / letters.length);
    }
  }

  async clearInput() {
    const characterList = this.getCharacterArray();
    for (var i = 0; i < characterList.length; i++) {
      characterList[i].setVisible(false);
      characterList[i].destroy();
    }
    // Wait a frame for the characters to be destroyed.
    await waitUntil(
      this.scene.time,
      () => this.getCharacterArray().length === 0,
    );
    this.setCursorIndex(0);
  }

  setInput(input: string) {
    this.clearInput();
    for (var i = 0; i < input.length; i++) {
      this.addCharacter(input[i]);
    }
  }

  moveCursorLeft() {
    this.setCursorIndex(Math.max(this.cursorIndex - 1, 0));
  }

  moveCursorRight() {
    this.setCursorIndex(
      Math.min(this.cursorIndex + 1, this.getCharacterArray().length),
    );
  }

  addCharacter(character: string, index?: number): SingleCharacterText {
    // If no index is specified, add the character at the cursor index.
    if (index === undefined) {
      index = this.cursorIndex;
    }
    const newCharacter = new SingleCharacterText(this.scene, 0, 0).setCharacter(
      character,
    );
    this.typedTextLayoutGroup.addElement(newCharacter);
    this.typedTextLayoutGroup.moveElement(newCharacter, index);
    this.setCursorIndex(index + 1);
    return newCharacter;
  }

  removeCharacter(index?: number) {
    // If no index is specified, remove the character at the cursor index.
    if (index === undefined) {
      index = this.cursorIndex;
    }
    if (index === 0) return; // The cursor is at the start of the input.
    const characterArray = this.getCharacterArray();
    const characterToRemove = characterArray[index - 1]; // -1 because the cursor is not a character
    this.typedTextLayoutGroup.removeElement(characterToRemove);
    this.setCursorIndex(index - 1);
  }

  getCharacterArray(): SingleCharacterText[] {
    const characterList = this.typedTextLayoutGroup.getElementList();

    return [
      ...characterList.filter(
        (element) => element instanceof SingleCharacterText,
      ),
    ];
  }

  updateCursorPosFromUserClick(event: any) {
    const x = event.x;
    const localX = x - this.typingBacking.getBounds().x;

    const closestLetter = this.getCharacterArray().reduce(
      (closest, current) => {
        const currentLocalPos =
          current.getBounds().x - this.typingBacking.getBounds().x;
        const closestLocalPos =
          closest.getBounds().x - this.typingBacking.getBounds().x;
        const currentDistance = Math.abs(currentLocalPos - localX);
        const closestDistance = Math.abs(closestLocalPos - localX);
        return currentDistance < closestDistance ? current : closest;
      },
      this.getCharacterArray()[0],
    );
    if (!closestLetter) return;

    //Figure out which side of the letter to put the cursor on.
    const letterLeft =
      closestLetter.getBounds().x - this.typingBacking.getBounds().x;
    const letterRight =
      closestLetter.getBounds().x +
      closestLetter.getBounds().width -
      this.typingBacking.getBounds().x;
    const side =
      Math.abs(letterLeft - localX) < Math.abs(letterRight - localX)
        ? 'left'
        : 'right';
    if (side === 'left') {
      this.setCursorIndex(this.getCharacterArray().indexOf(closestLetter));
    } else {
      this.setCursorIndex(this.getCharacterArray().indexOf(closestLetter) + 1);
    }
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
